import React, { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSite } from '@/contexts/SiteContext';
import { useCatalogAggregate, AggregateRow, CatalogDimension } from '@/hooks/useCatalog';
import { LinkedViewsStrip } from '@/components/catalog/LinkedViewsStrip';
import { UrlStateBadge } from '@/components/catalog/UrlStateBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, ChevronRight } from 'lucide-react';
import { catalogColors } from '@/components/catalog/catalogColors';
import { sourceLabel } from '@/components/catalog/sourceLabels';

// ─── Constants ────────────────────────────────────────────────────────────────

const DIMENSIONS: Array<{ value: CatalogDimension; label: string }> = [
  { value: 'category',                    label: 'Category' },
  { value: 'custom_label_4',              label: 'Custom label 4' },
  { value: 'brand',                       label: 'Brand' },
  { value: 'source',                      label: 'Source' },
  { value: 'aeo_status',                  label: 'AEO status' },
  { value: 'live_status',                 label: 'Live status' },
  { value: 'translation_coverage_bucket', label: 'Translation coverage' },
  { value: 'updated_bucket',              label: 'Updated' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function n(v: number | undefined) { return (v ?? 0).toLocaleString(); }

// D18: % Live bar inline in column
function PctBar({ pct }: { pct: number | undefined }) {
  const p = Number(pct ?? 0);
  const color = p >= 80 ? catalogColors.accentTeal : p >= 40 ? catalogColors.amber : catalogColors.warn;
  return (
    <div className="flex items-center gap-2">
      <div className="w-14 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${Math.min(100, p)}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs tabular-nums text-muted-foreground">{p.toFixed(1)}%</span>
    </div>
  );
}

// Count cell with per-stage % subscript (D18)
function CountCell({ count, pct, color }: { count: number; pct?: number; color?: string }) {
  return (
    <div className="text-right">
      <div className="tabular-nums font-semibold">{n(count)}</div>
      {pct !== undefined && pct > 0 && (
        <div className="text-[10px] tabular-nums" style={{ color: color ?? catalogColors.accentTeal }}>{Number(pct).toFixed(1)}%</div>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function CatalogPivot() {
  const { selectedSite } = useSite();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const siteId = selectedSite?.id || selectedSite?._id;
  const group  = (params.get('g') || 'category') as CatalogDimension;
  const group2 = (params.get('g2') || undefined) as CatalogDimension | undefined;
  const sortBy = params.get('s') || 'live:desc';
  const filters: Record<string, string> = {};
  params.forEach((v, k) => { if (k.startsWith('f.')) filters[k.slice(2)] = v; });

  const { data, isLoading, error } = useCatalogAggregate(siteId, group, group2, filters);

  const setGroup = useCallback((val: string) => {
    setParams(prev => { const p = new URLSearchParams(prev); p.set('g', val); p.delete('g2'); return p; }, { replace: true });
  }, [setParams]);

  const setGroup2 = useCallback((val: string) => {
    setParams(prev => { const p = new URLSearchParams(prev); if (val === '_none') p.delete('g2'); else p.set('g2', val); return p; }, { replace: true });
  }, [setParams]);

  const drillToMonitor = useCallback((row: AggregateRow) => {
    const p = new URLSearchParams();
    p.set(`f.${group}`, row.group_key);
    if (group2 && row.group_key2) p.set(`f.${group2}`, row.group_key2);
    navigate(`/catalog/monitor?${p.toString()}`);
  }, [navigate, group, group2]);

  // Sort client-side
  const rows = data?.rows ?? [];
  const targetLanguageCount = data?.target_language_count ?? 0;
  const showTranslated = targetLanguageCount > 0;
  const translatedHeader = `Translated (${targetLanguageCount}/${targetLanguageCount})`;
  const sorted = [...rows].sort((a, b) => {
    const [col, dir] = sortBy.split(':');
    const av = (a as any)[col] ?? 0;
    const bv = (b as any)[col] ?? 0;
    return dir === 'asc' ? av - bv : bv - av;
  });

  const grouped2 = group2
    ? sorted.reduce<Record<string, AggregateRow[]>>((acc, row) => {
        (acc[row.group_key] = acc[row.group_key] || []).push(row);
        return acc;
      }, {})
    : null;

  // D18: column headers — % Live replaced with Translated (N/N) when target_language_count > 0
  const COLS = showTranslated
    ? ['Catalog products', 'AEO candidates', 'AEO generated', 'Approved', 'Live on merchant site', translatedHeader]
    : ['Catalog products', 'AEO candidates', 'AEO generated', 'Approved', 'Live on merchant site'];

  return (
    <div className="p-6 max-w-[1440px] mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Catalog Pivot</h1>
          <p className="text-sm text-muted-foreground mt-1">Aggregate pipeline stages by dimension</p>
        </div>
        <LinkedViewsStrip />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Group by</span>
          <Select value={group} onValueChange={setGroup}>
            <SelectTrigger className="w-[180px] h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{DIMENSIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">then by</span>
          <Select value={group2 || '_none'} onValueChange={setGroup2}>
            <SelectTrigger className="w-[180px] h-8 text-sm"><SelectValue placeholder="(none)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">— none —</SelectItem>
              {DIMENSIONS.filter(d => d.value !== group).map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <UrlStateBadge />
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive p-3 rounded-lg bg-destructive/10">
          <AlertCircle className="h-4 w-4 shrink-0" />{(error as Error).message}
        </div>
      )}
      {!siteId && !isLoading && <div className="text-center py-16 text-muted-foreground">Select a site to view the pivot.</div>}
      {isLoading && <Skeleton className="h-72 rounded-lg" />}

      {!isLoading && !error && siteId && data && rows.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">
          No data yet for this site and dimension — try a different group or check back after the first catalog import.
        </div>
      )}

      {data && rows.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {DIMENSIONS.find(d => d.value === group)?.label}
              {group2 && <> × {DIMENSIONS.find(d => d.value === group2)?.label}</>}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b">
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Group</th>
                  {COLS.map(c => <th key={c} className="px-4 py-2 font-medium text-right">{c}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y">
                {grouped2
                  ? Object.entries(grouped2).map(([key, rows]) => {
                      const sub = rows.reduce((acc, r) => ({
                        ...acc,
                        catalog_products: acc.catalog_products + (r.catalog_products ?? r.active ?? 0),
                        aeo_candidates:   acc.aeo_candidates + (r.aeo_candidates ?? 0),
                        aeo_generated:    acc.aeo_generated + r.aeo_generated,
                        approved:         acc.approved + r.approved,
                        live:             acc.live + r.live,
                        fully_translated: acc.fully_translated + (r.fully_translated ?? 0),
                      }), { catalog_products: 0, aeo_candidates: 0, aeo_generated: 0, approved: 0, live: 0, fully_translated: 0 });
                      const subLivePct = sub.aeo_generated > 0 ? Math.round(1000 * sub.live / sub.aeo_generated) / 10 : 0;
                      const subTranslatedPct = sub.aeo_generated > 0 ? Math.round(sub.fully_translated / sub.aeo_generated * 100) : 0;
                      return (
                        <React.Fragment key={key}>
                          <tr className="bg-muted/40 font-medium">
                            <td className="px-4 py-2">{group === 'source' ? sourceLabel(key) : key}</td>
                            <td className="px-4 py-2 text-right tabular-nums">{n(sub.catalog_products)}</td>
                            <td className="px-4 py-2 text-right tabular-nums">{n(sub.aeo_candidates)}</td>
                            <td className="px-4 py-2"><CountCell count={sub.aeo_generated} pct={sub.aeo_candidates > 0 ? Math.round(1000 * sub.aeo_generated / sub.aeo_candidates) / 10 : 0} color={catalogColors.accentTeal} /></td>
                            <td className="px-4 py-2"><CountCell count={sub.approved} pct={sub.aeo_generated > 0 ? Math.round(1000 * sub.approved / sub.aeo_generated) / 10 : 0} color={catalogColors.accentDark} /></td>
                            <td className="px-4 py-2"><CountCell count={sub.live} pct={subLivePct} color={catalogColors.purple} /></td>
                            {showTranslated && <td className="px-4 py-2"><PctBar pct={subTranslatedPct} /></td>}
                          </tr>
                          {rows.map(row => (
                            <tr key={`${key}-${row.group_key2}`} className="hover:bg-muted/20 cursor-pointer" onClick={() => drillToMonitor(row)}>
                              <td className="px-4 py-1.5 pl-8 text-muted-foreground">
                                <span className="flex items-center gap-1">{(group2 === 'source' ? sourceLabel(row.group_key2) : row.group_key2) || '—'} <ChevronRight className="h-3 w-3" /></span>
                              </td>
                              <td className="px-4 py-1.5 text-right tabular-nums">{n(row.catalog_products ?? row.active)}</td>
                              <td className="px-4 py-1.5 text-right tabular-nums">{n(row.aeo_candidates)}</td>
                              <td className="px-4 py-1.5"><CountCell count={row.aeo_generated} pct={row.pct_aeo_generated} color={catalogColors.accentTeal} /></td>
                              <td className="px-4 py-1.5"><CountCell count={row.approved} pct={row.pct_approved} color={catalogColors.accentDark} /></td>
                              <td className="px-4 py-1.5"><CountCell count={row.live} pct={row.pct_live} color={catalogColors.purple} /></td>
                              {showTranslated && <td className="px-4 py-1.5"><PctBar pct={row.aeo_generated > 0 ? Math.round((row.fully_translated ?? 0) / row.aeo_generated * 100) : 0} /></td>}
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })
                  : sorted.map(row => (
                      <tr key={row.group_key} className="hover:bg-muted/20 cursor-pointer" onClick={() => drillToMonitor(row)}>
                        <td className="px-4 py-2 flex items-center gap-1">
                          {(group === 'source' ? sourceLabel(row.group_key) : row.group_key) || '—'} <ChevronRight className="h-3 w-3 text-muted-foreground" />
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">{n(row.catalog_products ?? row.active)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{n(row.aeo_candidates)}</td>
                        <td className="px-4 py-2"><CountCell count={row.aeo_generated} pct={row.pct_aeo_generated} color={catalogColors.accentTeal} /></td>
                        <td className="px-4 py-2"><CountCell count={row.approved} pct={row.pct_approved} color={catalogColors.accentDark} /></td>
                        <td className="px-4 py-2"><CountCell count={row.live} pct={row.pct_live} color={catalogColors.purple} /></td>
                        {showTranslated && <td className="px-4 py-2"><PctBar pct={row.aeo_generated > 0 ? Math.round((row.fully_translated ?? 0) / row.aeo_generated * 100) : 0} /></td>}
                      </tr>
                    ))
                }
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
